import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import { ExpandMore, Clear, Search } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';

export interface SearchFilters {
  query?: string;
  title?: string;
  author?: string;
  isbn?: string;
  publisher?: string;
  categoryIds?: number[];
  minPrice?: number;
  maxPrice?: number;
  minYear?: number;
  maxYear?: number;
  language?: string;
  inStock?: boolean;
  featured?: boolean;
  sortBy?: string;
}

interface AdvancedSearchProps {
  onSearch: (filters: SearchFilters) => void;
  onClear: () => void;
  availableFilters?: {
    categories: Array<{ id: number; name: string; productCount: number }>;
    priceRange: { minPrice: string; maxPrice: string };
    yearRange: { minYear: number; maxYear: number };
    publishers: string[];
    languages: string[];
  };
  loading?: boolean;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  onSearch,
  onClear,
  availableFilters,
  loading = false,
}) => {
  const [filters, setFilters] = useState<SearchFilters>({
    sortBy: 'relevance',
  });

  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [yearRange, setYearRange] = useState<[number, number]>([2000, new Date().getFullYear()]);

  useEffect(() => {
    if (availableFilters?.priceRange) {
      const min = parseFloat(availableFilters.priceRange.minPrice) || 0;
      const max = parseFloat(availableFilters.priceRange.maxPrice) || 1000;
      setPriceRange([min, max]);
      setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }));
    }
    if (availableFilters?.yearRange) {
      const min = availableFilters.yearRange.minYear || 2000;
      const max = availableFilters.yearRange.maxYear || new Date().getFullYear();
      setYearRange([min, max]);
      setFilters(prev => ({ ...prev, minYear: min, maxYear: max }));
    }
  }, [availableFilters]);

  const handleInputChange = (field: keyof SearchFilters) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: keyof SearchFilters) => (
    event: SelectChangeEvent<string | number[]>
  ) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCheckboxChange = (field: keyof SearchFilters) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handlePriceChange = (event: Event, newValue: number | number[]) => {
    const value = newValue as [number, number];
    setPriceRange(value);
    setFilters(prev => ({
      ...prev,
      minPrice: value[0],
      maxPrice: value[1],
    }));
  };

  const handleYearChange = (event: Event, newValue: number | number[]) => {
    const value = newValue as [number, number];
    setYearRange(value);
    setFilters(prev => ({
      ...prev,
      minYear: value[0],
      maxYear: value[1],
    }));
  };

  const handleSearch = () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => 
        value !== undefined && value !== '' && 
        (Array.isArray(value) ? value.length > 0 : true)
      )
    );
    onSearch(cleanFilters);
  };

  const handleClear = () => {
    setFilters({ sortBy: 'relevance' });
    if (availableFilters?.priceRange) {
      const min = parseFloat(availableFilters.priceRange.minPrice) || 0;
      const max = parseFloat(availableFilters.priceRange.maxPrice) || 1000;
      setPriceRange([min, max]);
    }
    if (availableFilters?.yearRange) {
      const min = availableFilters.yearRange.minYear || 2000;
      const max = availableFilters.yearRange.maxYear || new Date().getFullYear();
      setYearRange([min, max]);
    }
    onClear();
  };

  const getActiveFilterCount = () => {
    return Object.entries(filters).filter(([key, value]) => 
      key !== 'sortBy' && value !== undefined && value !== '' && 
      (Array.isArray(value) ? value.length > 0 : true)
    ).length;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Advanced Search
          </Typography>
          {getActiveFilterCount() > 0 && (
            <Chip 
              label={`${getActiveFilterCount()} filters active`} 
              size="small" 
              color="primary" 
            />
          )}
        </Box>

        <Grid container spacing={2}>
          {/* Basic Search Fields */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Search query"
              value={filters.query || ''}
              onChange={handleInputChange('query')}
              placeholder="Search books, authors, or keywords..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              value={filters.title || ''}
              onChange={handleInputChange('title')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Author"
              value={filters.author || ''}
              onChange={handleInputChange('author')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="ISBN"
              value={filters.isbn || ''}
              onChange={handleInputChange('isbn')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Publisher"
              value={filters.publisher || ''}
              onChange={handleInputChange('publisher')}
            />
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Categories & Filters</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Categories */}
              {availableFilters?.categories && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Categories</InputLabel>
                    <Select
                      multiple
                      value={filters.categoryIds || []}
                      onChange={handleSelectChange('categoryIds')}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as number[]).map((value) => {
                            const category = availableFilters.categories.find(c => c.id === value);
                            return <Chip key={value} label={category?.name || value} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {availableFilters.categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name} ({category.productCount})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Language */}
              {availableFilters?.languages && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={filters.language || ''}
                      onChange={handleSelectChange('language')}
                    >
                      <MenuItem value="">All languages</MenuItem>
                      {availableFilters.languages.map((language) => (
                        <MenuItem key={language} value={language}>
                          {language}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Sort By */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    value={filters.sortBy || 'relevance'}
                    onChange={handleSelectChange('sortBy')}
                  >
                    <MenuItem value="relevance">Relevance</MenuItem>
                    <MenuItem value="price_asc">Price: Low to High</MenuItem>
                    <MenuItem value="price_desc">Price: High to Low</MenuItem>
                    <MenuItem value="title_asc">Title: A to Z</MenuItem>
                    <MenuItem value="title_desc">Title: Z to A</MenuItem>
                    <MenuItem value="date_desc">Newest First</MenuItem>
                    <MenuItem value="popularity">Most Popular</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Checkboxes */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.inStock || false}
                      onChange={handleCheckboxChange('inStock')}
                    />
                  }
                  label="In stock only"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.featured || false}
                      onChange={handleCheckboxChange('featured')}
                    />
                  }
                  label="Featured items only"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Price Range */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Price Range</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography gutterBottom>
              Price: ${priceRange[0]} - ${priceRange[1]}
            </Typography>
            <Slider
              value={priceRange}
              onChange={handlePriceChange}
              valueLabelDisplay="auto"
              min={0}
              max={Math.max(1000, priceRange[1])}
              step={10}
            />
          </AccordionDetails>
        </Accordion>

        {/* Year Range */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Publication Year</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography gutterBottom>
              Year: {yearRange[0]} - {yearRange[1]}
            </Typography>
            <Slider
              value={yearRange}
              onChange={handleYearChange}
              valueLabelDisplay="auto"
              min={1900}
              max={new Date().getFullYear()}
              step={1}
            />
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={loading}
            sx={{ flexGrow: 1 }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdvancedSearch;